import React, { useEffect, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAuth } from '../contexts/AuthContext'
import { apiClient } from '../api/client'
import { Button, Card, Alert } from '../components/ui'

interface InvitationInfo {
  email: string
  role: string
  org_name: string
  org_id: string
}

const KNOWN_ROLES = new Set(['owner', 'admin', 'member', 'respondent'])

const AcceptInvitationPage: React.FC = () => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()

  const [invitation, setInvitation] = useState<InvitationInfo | null>(null)
  const [isValidating, setIsValidating] = useState(true)
  const [isAccepting, setIsAccepting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Localized, lowercased role noun for inline use ("as a member").
  const roleLabel = (role?: string): string => {
    const key = (role || 'member').toLowerCase()
    return KNOWN_ROLES.has(key)
      ? intl.formatMessage({ id: `role.${key}` })
      : (role || '').toLowerCase()
  }

  // Validate the token on mount
  useEffect(() => {
    if (!token) {
      setError(intl.formatMessage({ id: 'invite.noToken' }))
      setIsValidating(false)
      return
    }

    const validate = async () => {
      try {
        const response = await apiClient.get(`/invitations/accept?token=${encodeURIComponent(token)}`)
        setInvitation(response.data.invitation)
      } catch (err: any) {
        const detail = err.response?.data?.detail || intl.formatMessage({ id: 'invite.invalidLink' })
        setError(detail)
      } finally {
        setIsValidating(false)
      }
    }

    validate()
  }, [token, intl])

  const handleAccept = async () => {
    setIsAccepting(true)
    setError('')
    try {
      const response = await apiClient.post(`/invitations/accept?token=${encodeURIComponent(token)}`)
      setSuccess(true)
      // Redirect to organization page after a short delay
      setTimeout(() => {
        navigate(`/organizations/${response.data.org_id}`)
      }, 1500)
    } catch (err: any) {
      const detail = err.response?.data?.detail || intl.formatMessage({ id: 'invite.failedAccept' })
      setError(detail)
    } finally {
      setIsAccepting(false)
    }
  }

  // Wait for both auth and validation to resolve
  if (authLoading || isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-foreground-muted"><FormattedMessage id="invite.validating" /></p>
        </div>
      </div>
    )
  }

  // Error state
  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
        <div className="max-w-md w-full text-center">
          <Card padding="lg">
            <div className="text-4xl mb-4">&#x26A0;&#xFE0F;</div>
            <h2 className="text-xl font-bold text-foreground mb-2"><FormattedMessage id="invite.problemTitle" /></h2>
            <p className="text-sm text-foreground-muted mb-6">{error}</p>
            <Link to="/dashboard">
              <Button variant="primary"><FormattedMessage id="invite.goDashboard" /></Button>
            </Link>
          </Card>
        </div>
      </div>
    )
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
        <div className="max-w-md w-full text-center">
          <Card padding="lg">
            <div className="text-4xl mb-4">&#x1F389;</div>
            <h2 className="text-xl font-bold text-foreground mb-2"><FormattedMessage id="invite.welcome" /></h2>
            <p className="text-sm text-foreground-muted mb-2">
              <FormattedMessage
                id="invite.joinedAs"
                values={{ org: <strong>{invitation?.org_name}</strong>, role: roleLabel(invitation?.role) }}
              />
            </p>
            <p className="text-xs text-foreground-muted"><FormattedMessage id="invite.redirecting" /></p>
          </Card>
        </div>
      </div>
    )
  }

  // Valid invitation — show details
  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
      <div className="max-w-md w-full">
        <Card padding="lg">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-foreground">Sondely</h1>
          </div>

          <h2 className="text-xl font-semibold text-foreground mb-2"><FormattedMessage id="invite.title" /></h2>
          <p className="text-sm text-foreground-muted mb-6">
            <FormattedMessage
              id="invite.invitedAs"
              values={{ org: <strong>{invitation?.org_name}</strong>, role: <strong>{roleLabel(invitation?.role)}</strong> }}
            />
          </p>

          {error && (
            <Alert variant="error" className="mb-4">{error}</Alert>
          )}

          {user ? (
            // Logged in — show accept button
            <div>
              <p className="text-xs text-foreground-muted mb-4">
                <FormattedMessage id="invite.signedInAs" values={{ email: <strong>{user.email}</strong> }} />
              </p>
              <Button
                onClick={handleAccept}
                loading={isAccepting}
                fullWidth
              >
                {isAccepting
                  ? intl.formatMessage({ id: 'invite.joining' })
                  : intl.formatMessage({ id: 'invite.accept' })}
              </Button>
            </div>
          ) : (
            // Not logged in — show auth links
            <div className="space-y-3">
              <p className="text-xs text-foreground-muted mb-4">
                <FormattedMessage id="invite.signInOrCreate" />
              </p>
              <Link to={`/auth/signin?redirect=${encodeURIComponent(`/invitations/accept?token=${token}`)}`}>
                <Button variant="primary" fullWidth>
                  <FormattedMessage id="invite.signInToAccept" />
                </Button>
              </Link>
              <Link to={`/auth/signup?redirect=${encodeURIComponent(`/invitations/accept?token=${token}`)}`}>
                <Button variant="outline" fullWidth>
                  <FormattedMessage id="invite.createAccount" />
                </Button>
              </Link>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

export default AcceptInvitationPage
